import { createFileRoute, Navigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { AppBranding } from '@/components/Auth/AppBranding';
import { GoogleButton } from '@/components/Auth/GoogleButton';
import { LoadingScreen } from '@/components/Layout/LoadingScreen';
import { MuiCard, MuiCardContent, MuiCardHeader, MuiContainer } from '@/components/mui';
import { config } from '@/config/env';
import { getAuthErrorMessage, useAuth } from '@/lib/authClient';
import { showError } from '@/stores/errorStore';

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: z.object({
    error: z.string().optional(),
  }),
});

function LoginPage() {
  const [loading, setLoading] = useState(false);
  const { signIn, isPending, isAuthenticated } = useAuth();
  const { error } = useSearch({ from: '/login' });

  useEffect(() => {
    if (error) {
      showError(getAuthErrorMessage(error));
    }
  }, [error]);

  async function handleGoogleLogin() {
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
    }, 5000);

    const { error: signInError } = await signIn.social({
      provider: 'google',
      callbackURL: config.googleOAuthRedirectUrl,
      errorCallbackURL: `${config.googleOAuthRedirectUrl}/login`,
    });

    if (signInError) {
      setLoading(false);
      showError(signInError.message ?? 'A sign-in error occurred');
    }
  }

  // Show loading while checking auth
  if (isPending) {
    return <LoadingScreen message="Loading app..." />;
  }

  // Redirect if already authenticated - use replace to avoid back button issues
  if (isAuthenticated) {
    return <Navigate replace to="/deals" />;
  }

  return (
    <MuiContainer maxWidth="sm" sx={{ pt: { xs: 4, md: 8 }, pb: 4 }}>
      {/* App Logo and Branding */}
      <AppBranding showSubtitle />

      <MuiCard
        elevation={3}
        sx={{
          maxWidth: 400,
          margin: '0 auto',
          borderRadius: 3,
        }}
      >
        <MuiCardHeader
          slotProps={{
            title: {
              textAlign: 'center',
              variant: 'h5',
              fontWeight: 600,
            },
          }}
          sx={{ pb: 1 }}
          title="Welcome Back"
        />

        <MuiCardContent sx={{ pt: 0, px: 3, pb: 3 }}>
          <GoogleButton isLoading={isPending || loading} onClick={() => handleGoogleLogin()} />
        </MuiCardContent>
      </MuiCard>
    </MuiContainer>
  );
}
